<script setup lang="ts">
import { computed } from 'vue';

import { useProductStyle } from '@/composables/useProductStyle';
import { SKU_COLLAPSE_STYLE, SKU_STYLE_TYPE } from '@/store/constants';
import { globalConfig } from '@/store/globalConfig';
import { useGlobalStyle, useTemplate } from '@/store/templateData';

import ModuleStreamRoomAndProductFlattenDisplay from '../ModuleStreamRoomAndProductFlattenDisplay/ModuleStreamRoomAndProductFlattenDisplay.vue';
import ModuleStreamRoomAndProductSkuDisplay from '../ModuleStreamRoomAndProductSkuDisplay/ModuleStreamRoomAndProductSkuDisplay.vue';

interface ModuleStreamRoomAndProductProps {
  module: {
    components: {
      title: {
        text: string;
      };
      subtitle: {
        text: string;
      };
      background: {
        src: string;
      };
      name: {
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      skuStyle: {
        type: 'expand' | 'collapse';
        collapseStyle: 'square' | 'circle' | 'hidden';
      };
      originalPrice: {
        enabled: boolean;
        textChoice: number;
      };
      price: {
        enabled: boolean;
        textSize: string;
        lineHeight: string;
        textColor: string;
      };
      shopcart: {
        enabled: boolean;
      };
      button: {
        text: string;
        textSize: string;
        textColor: string;
        lineHeight: string;
        fillStyle: 'outline' | 'solid';
      };
    };
  };
}

defineOptions({
  name: 'ModuleStreamRoomAndProductDisplay'
});

const props = withDefaults(defineProps<ModuleStreamRoomAndProductProps>(), {
  module: () => ({
    components: {
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      background: {
        src: '',
      },
      name: {
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      skuStyle: {
        type: SKU_STYLE_TYPE.EXPAND as 'expand',
        collapseStyle: SKU_COLLAPSE_STYLE.SQUARE as 'square',
      },
      originalPrice: {
        enabled: true,
        textChoice: 0,
      },
      price: {
        enabled: true,
        textSize: '',
        lineHeight: '',
        textColor: '',
      },
      shopcart: {
        enabled: true,
      },
      button: {
        text: '',
        textSize: '',
        textColor: '',
        lineHeight: '',
        fillStyle: 'outline' as 'outline',
      },
    },
  }),
});

const { isGeneralCmsStreamCanPlay } = useTemplate();

const components = computed(() => props.module.components);

const needRender = computed(() => [
  isGeneralCmsStreamCanPlay.value,
].some(Boolean));

const shouldFlattenProductSkuList = computed(() => {
  return components.value.skuStyle.type === SKU_STYLE_TYPE.EXPAND;
});

const localStreamId = globalConfig.value.localStreamId;

const { titleStyle, subtitleStyle } = useGlobalStyle();
const productStyleVars = useProductStyle(components);
</script>

<template>
<div class="module-stream-room-and-product-display" v-if="needRender" :style="productStyleVars">
  <div class="module-stream-room-and-product-display__header">
    <div
      class="module-stream-room-and-product-display__title"
      v-if="components.title.text"
      :style="titleStyle"
    >
      {{ components.title.text }}
    </div>
    <div
      class="module-stream-room-and-product-display__subtitle"
      v-if="components.subtitle.text"
      :style="subtitleStyle"
    >
      {{ components.subtitle.text }}
    </div>
  </div>

  <div class="module-stream-room-and-product-display__submodule">
    <div class="module-stream-room-and-product-display__wrapper">
      <div class="module-stream-room-and-product-display__box">
        <div class="module-stream-room-and-product-display__stream" :id="localStreamId"></div>
      </div>
    </div>

    <template v-if="shouldFlattenProductSkuList">
      <ModuleStreamRoomAndProductFlattenDisplay :module="module"/>
    </template>
    <template v-else>
      <ModuleStreamRoomAndProductSkuDisplay :module="module"/>
    </template>
  </div>
</div>
</template>
<style lang="scss" scoped>
$width: 375px;
$height: 666px;
$desktop-fixed-width: 1178px;
$stream-room-bg: #D4D5D7;

.module-stream-room-and-product-display {
  @apply px-0 py-4;

  &__header {
    @apply mx-5;
  }

  &__title,
  &__subtitle {
    @apply font-medium break-all;
    // mobile版直播間滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }

  &__title {
    @apply text-4xl text-center whitespace-pre-line;
  }

  &__subtitle {
    @apply mt-4 text-xl text-left whitespace-pre-line;
  }

  &__submodule {
    @apply mt-6;
    @apply grid gap-8;
  }

  &__wrapper {
    @apply relative w-full;

    background-color: $stream-room-bg;

    @include aspect-ratio($height: 16, $width: 9);
  }

  &__box {
    @apply absolute inset-0 h-full w-full bg-center bg-cover;
  }

  &__stream {
    @apply mx-auto w-full h-full bg-black flex items-center justify-center;
  }

  &__play-icon {
    @apply w-12 h-12;
  }
}

.tablet .module-stream-room-and-product-display {
  &__header {
    @apply mx-6;
  }

  &__wrapper {
    @apply pb-0;

    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-10;
  }
}

.laptop .module-stream-room-and-product-display {
  @apply px-6 py-8;
  @apply w-full;

  max-width: calc(#{$desktop-fixed-width} + 2 * 24px); // include px-6;

  &__header {
    @apply mx-6;
  }

  &__submodule {
    @apply grid gap-8;

    grid-template-columns: auto 1fr;
  }

  &__wrapper {
    @apply mt-0 pb-0;

    width: $width;
    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-0;
  }
}

.desktop .module-stream-room-and-product-display {
  @apply px-6 py-8;

  width: $desktop-fixed-width;

  &__header {
    @apply mx-0;
  }

  &__submodule {
    @apply grid gap-8 mt-8;

    grid-template-columns: auto 1fr;
  }

  &__wrapper {
    @apply mt-0 pb-0;

    width: $width;
    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-0;
  }
}
</style>
